import { z } from 'zod';
import { compararDinheiro, somarDinheiro, subtrairDinheiro } from './aritmetica.ts';
import { dataSchema, dinheiroSchema, uuidSchema } from './comum.ts';
import { confrontoSchema, serieDeValoresSchema } from './painel-titulos.ts';
import { faturamentoPorNotaSchema } from './saldo-a-faturar.ts';

/**
 * Painel de obras — o que a obra deve, o que ela tem a receber e o que ela
 * consumiu (`.spec/010`, módulo Contratos; `.spec/016`, seção 10).
 *
 * É **só leitura e só agregação**, como os quatro painéis anteriores: nenhum
 * título, baixa ou consumo nasce, muda ou some por aqui. Ele soma o que o
 * rateio dos títulos e os lançamentos de consumo gravaram.
 *
 * ## O que ele responde, e que os outros painéis não respondem
 *
 * **Se a obra se paga.** PAG·02 e REC·02 respondem "quanto o grupo deve" e
 * "quanto o grupo tem a receber"; FIN·06 cruza os dois pelo caixa da empresa.
 * Nenhum deles chega à pergunta de quem toca uma obra — quanto custou este
 * canteiro, quanto ele já trouxe, e em que o dinheiro foi gasto. Ela exige um
 * recorte que aqueles três não têm e um dado que eles não olham: o **consumo**,
 * que não passa pelo contas a pagar.
 *
 * ## Tudo é recortado **pela obra**, e essa é a decisão central
 *
 * Empresa, cliente e obra não filtram o título: eles filtram a **obra**, e o
 * título entra porque tem uma fatia de rateio numa obra que sobrou do filtro. É
 * a diferença mais importante em relação a PAG·02 e REC·02, onde `empresaId`
 * compara com `contas_pagar.empresa_id`, do cabeçalho do título.
 *
 * A escolha é o que torna a tela coerente: consumo é da obra por natureza —
 * `lancamentos_consumo.obra_id` é obrigatório —, e se os títulos entrassem pela
 * empresa do cabeçalho, o recorte "empresa X" somaria o custo das obras de X com
 * títulos que nada têm com obra alguma. O usuário leria os quatro números da tela
 * como quatro leituras do mesmo conjunto, e eles não seriam.
 *
 * A consequência tem de ser dita na tela, e é: **o título sem rateio nunca
 * aparece aqui.** Ele é a compra de almoxarifado central, a conta de energia do
 * escritório, a receita administrativa — dinheiro que não pertence a canteiro
 * nenhum. Em PAG·02 isso é um efeito colateral do filtro de obra; aqui é a
 * natureza do painel, e vale mesmo sem filtro nenhum escolhido.
 *
 * ## O título entra pela **fatia**, não inteiro
 *
 * O valor de cada título é `saldo × índice de rateio ÷ 100`, e não o saldo
 * inteiro. É a segunda diferença em relação a PAG·02, e a razão é aritmética: um
 * título rateado 60% na Aurora e 40% na Bela Vista, contado inteiro, apareceria
 * duas vezes num recorte que alcance as duas — e a soma das obras daria 160% do
 * que a empresa deve. PAG·02 pode contá-lo inteiro porque responde "o que se
 * deve", e o que se deve não se paga em pedaços; aqui a pergunta é "quanto custou
 * esta obra", e a resposta é a fatia dela.
 *
 * ## Um período só, e ele alcança tudo
 *
 * Este é o único painel do sistema com **um relógio**, e a escolha é o que o
 * distingue dos outros quatro. Lá convivem duas janelas — a fila sem recorte de
 * data e os últimos 30 dias fixos —, porque a pergunta daquelas telas é sobre o
 * agora. Aqui a pergunta é sobre um **trecho da obra**: "o trimestre que fechou",
 * "desde que a fundação começou", "o mês da medição". Nessa leitura, quatro
 * números com três recortes diferentes não se somam nem se comparam — e o
 * usuário os compararia mesmo assim.
 *
 * Cada número lê a data que é dele:
 *
 * - **A pagar e a receber**, pela **data de vencimento** do título. É quando o
 *   dinheiro está previsto para se mover, e é a data pela qual se planeja.
 * - **Pago e recebido**, pela **data da movimentação da baixa**. É quando o
 *   dinheiro se moveu de fato — a baixa lançada hoje com data de ontem se moveu
 *   ontem.
 * - **O consumo**, pela **data do lançamento**. É o dia em que o material saiu do
 *   depósito.
 *
 * São três colunas diferentes de propósito: uma só serviria a um dos três e
 * mentiria para os outros dois. O que elas têm em comum é responderem à mesma
 * pergunta — "quando isto aconteceu, ou vai acontecer" —, e é por isso que um
 * período único as recorta sem produzir número sem sentido.
 *
 * **As duas pontas são opcionais e independentes.** Vazias, o painel é a obra
 * inteira, desde o primeiro lançamento — que é a leitura de custo acumulado, e é
 * como a tela abre.
 *
 * ## O que ele não tem, e por quê
 *
 * **Não há faixa de vencimento.** Ela existiu na primeira versão desta tela e
 * saiu: faixa é a leitura de quem administra uma fila de pagamento — "o que
 * aperta nesta semana" —, e essa pergunta já tem três telas (PAG·02, REC·02 e
 * FIN·06), todas mais fundas. Aqui a pergunta é de resultado de obra, e o eixo do
 * tempo é o período escolhido, não a distância até hoje. As duas leituras na
 * mesma tela obrigariam a faixa a ignorar o período ou o período a partir a
 * faixa, e nenhuma das duas se explica ao usuário.
 */

/**
 * O recorte do painel: três campos de "quem" e um período que vale para tudo.
 *
 * Escrito aqui, e não reaproveitado dos outros painéis, pelo mesmo motivo que
 * eles não compartilham o deles: a consulta é a fronteira pública desta rota. E
 * aqui a divergência já existe — os três primeiros campos recortam **a obra**,
 * não o título.
 *
 * Ausentes significam "tudo que o usuário alcança": o recorte por empresa
 * vinculada continua sendo do servidor, como em toda listagem.
 *
 * **`clienteId` casa com `obras.cliente_id`**, e não com o cliente do contrato
 * da obra nem com o cliente do título a receber. A coluna existe justamente
 * porque obra e contrato podem discordar — há obra tocada para um cliente antes
 * da assinatura, e `obras.cliente_id` é onde esse nome está gravado. Derivar o
 * cliente do contrato deixaria esse caso de fora, e cruzar os dois produziria um
 * recorte que ninguém sabe explicar na tela.
 *
 * **`de` e `ate` são um par só, e alcançam os quatro números** — cada um pela
 * data que é dele. Ver o comentário do módulo: são três colunas diferentes, e
 * essa é a razão de o período ser um campo do painel e não de cada bloco.
 *
 * **`ate` anterior a `de` devolve painel zerado**, e não erro. É o mesmo
 * comportamento do cardex, que também tem as duas pontas opcionais: a ordem é
 * evidente na tela, o dano é uma consulta vazia, e uma validação aqui cobraria um
 * `superRefine` que a próxima ponta opcional esqueceria de repetir.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const painelDeObrasQuerySchema = z
  .object({
    /** Empresa **da obra** (`obras.empresa_id`), não a do cabeçalho do título. */
    empresaId: uuidSchema.optional(),
    /** Cliente **da obra** (`obras.cliente_id`). Ver acima. */
    clienteId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
    /** Primeiro dia contado, inclusivo. Ausente: desde o começo da obra. */
    de: dataSchema.optional(),
    /** Último dia contado, inclusivo. Ausente: até o último movimento. */
    ate: dataSchema.optional(),
  })
  .strict();

export type PainelDeObrasQuery = z.infer<typeof painelDeObrasQuerySchema>;

/* -------------------------------------------------------------------------- */
/* O consumo                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Quantos lançamentos de consumo e quanto de material.
 *
 * A quantidade conta **lançamentos**, e não insumos nem quantidade física: as
 * unidades não se somam — doze sacos mais três horas de guindaste não dão quinze
 * de nada —, e o número de lançamentos é o que diz se aqueles R$ 80.000,00 são
 * uma entrega grande ou trezentas retiradas de almoxarifado.
 *
 * O valor é `lancamentos_consumo_itens.valor_total`, que é quantidade × **custo
 * médio vigente no instante da baixa**. Ele não muda depois: o custo médio de
 * hoje não reprecifica o cimento que saiu em março, e é por isso que a soma de
 * um período fechado continua devolvendo o mesmo número no mês seguinte.
 *
 * ## A devolução abate, na data dela (decisão 82)
 *
 * `valor` é o **líquido**: o consumido menos o devolvido no período. É o custo
 * de material da obra, e é o que o painel e o relatório da obra mostram.
 * `valorConsumido` e `valorDevolvido` vêm ao lado porque a tela diz de onde o
 * líquido saiu — "R$ 1.250,00 devolvidos" sob o número —, e porque um período
 * que só tem a devolução (o material saiu em fevereiro e voltou em abril; o
 * filtro é abril) dá líquido negativo, e sem as parcelas isso pareceria defeito.
 */
export const totalDoConsumoSchema = z.object({
  /** Quantos lançamentos de consumo entraram na soma. */
  quantidade: z.number().int().nonnegative(),
  /** Consumido menos devolvido, no período. Pode ser negativo — ver acima. */
  valor: dinheiroSchema,
  /** Soma de `valor_total` dos itens de consumo — o bruto. */
  valorConsumido: dinheiroSchema,
  /** Soma de `valor_total` dos itens de devolução de consumo — o crédito. */
  valorDevolvido: dinheiroSchema,
  /** Quantas devoluções entraram na soma. */
  devolucoes: z.number().int().nonnegative(),
});

export type TotalDoConsumo = z.infer<typeof totalDoConsumoSchema>;

/* -------------------------------------------------------------------------- */
/* O plano de contas, com os dois lados do pagamento                          */
/* -------------------------------------------------------------------------- */

/**
 * Uma conta contábil com **as três colunas do título**: quanto ele vale, quanto
 * já se pagou dele e quanto ainda falta.
 *
 * Um título de R$ 12.000,00 com R$ 3.500,00 baixados aparece como
 * `valor` R$ 12.000,00, `pago` R$ 3.500,00 e `emAberto` R$ 8.500,00 — e
 * **`valor` é sempre `pago + emAberto`**, em toda linha. Não é coincidência: os
 * três saem das mesmas linhas dos mesmos títulos, e `pago` é literalmente
 * calculado como a diferença dos outros dois (ver o repositório). Uma linha em
 * que a conta não fechasse seria defeito, e é o tipo de coisa que quem confere
 * soma no papel.
 *
 * ## Por que as três na mesma linha, e não três gráficos
 *
 * Porque a pergunta é comparativa e a comparação é **por conta**: "quanto da
 * obrigação de mão de obra já foi honrada?". Três gráficos respondem isso
 * obrigando o olho a achar a mesma barra em três ordens diferentes — e as ordens
 * discordam de verdade, porque a conta de maior obrigação raramente é a de maior
 * saldo. Numa linha só, a comparação é horizontal e não exige procurar nada.
 *
 * É também por isso que é **tabela e não gráfico**: aqui o que se lê é o número,
 * conta a conta, para levar à reunião de obra. A distribuição de relance já está
 * nos dois gráficos de consumo logo abaixo.
 *
 * ## O que cada coluna recorta, e a armadilha do nome
 *
 * As três olham para os títulos que **vencem no período** — é o recorte do
 * cartão "A pagar" do topo. Dentro deles:
 *
 * - `valor` é o valor original do título, que não muda com as baixas.
 * - `pago` é quanto daquele título já foi baixado, **em qualquer data**.
 * - `emAberto` é o saldo, e é a coluna que fecha com o cartão "A pagar".
 *
 * **`pago` aqui não é o cartão "Pago" do bloco do realizado**, e a diferença tem
 * de ser dita na tela: lá a pergunta é "quanto saiu do caixa no período" — baixas
 * pela data da movimentação, de títulos de qualquer vencimento. Aqui é "quanto
 * desta obrigação já foi honrada", sem recorte de data na baixa. Os dois números
 * são legítimos, respondem coisas diferentes, e só coincidem por acaso.
 *
 * O critério é o que faz a linha fechar: com `pago` recortado pela data da baixa,
 * `valor` deixaria de ser `pago + emAberto` — e a coluna que sobrasse seria a
 * diferença entre dois recortes que não se correspondem.
 *
 * ## O título quitado entra
 *
 * Um título que venceu no período e já foi pago inteiro aparece com `emAberto`
 * zerado. Ele não some, e é de propósito: é justamente o custo que a obra já
 * absorveu, e escondê-lo faria a coluna `valor` contar menos do que a obra
 * assumiu no período. Como ele soma zero em `emAberto`, a coluna continua
 * fechando com o cartão.
 *
 * **Não há corte de topo nem fatia "Outros"**, ao contrário dos gráficos. Duas
 * razões: a tabela existe para ser conferida linha a linha, e o número de contas
 * folha que uma obra usa é limitado pelo plano de contas — dezenas, não milhares.
 * Um corte aqui quebraria justamente a soma que a torna útil.
 *
 * **Só o lado que paga.** Não há coluna do lado que recebe, e a ausência é de
 * modelo, não de espaço: o plano de contas de custo e o de receita não
 * compartilham uma única folha, e as colunas ficariam sempre zeradas em cada
 * linha. É a mesma razão pela qual FIN·06 não confronta os dois lados por conta
 * contábil.
 */
export const linhaDeContaContabilSchema = z.object({
  /**
   * Id da conta contábil.
   *
   * **Nunca há linha "sem conta" aqui**, ao contrário do gráfico de consumo: a
   * classificação de um título é obrigatória e fecha em 100%
   * (`contas_pagar_contas_contabeis`), enquanto a do insumo é opcional. A
   * diferença é de modelo, e é o que permite à tabela não ter linha de resto.
   */
  chave: z.string(),
  /** Código e descrição, que é como o plano de contas se lê. */
  rotulo: z.string(),
  /**
   * Valor original dos títulos que vencem no período, rateado por obra e por
   * conta. Não muda com as baixas — quem anda é o saldo.
   */
  valor: dinheiroSchema,
  /** Quanto desses títulos já foi baixado, em qualquer data. `valor − emAberto`. */
  pago: dinheiroSchema,
  /** O saldo que ainda falta. É a coluna que fecha com o cartão "A pagar". */
  emAberto: dinheiroSchema,
});

export type LinhaDeContaContabil = z.infer<typeof linhaDeContaContabilSchema>;

/* -------------------------------------------------------------------------- */
/* Orçado × realizado, por etapa da obra                                      */
/* -------------------------------------------------------------------------- */

/**
 * O confronto entre o que se orçou e o que se gastou, **etapa por etapa**
 * (`.spec/015`, decisão 60).
 *
 * É o que a decisão 59 preparou: a parte da obra nasce apontando para a linha do
 * orçamento, e o custo já sabe a que etapa pertence. As duas pontas passam a ter
 * o mesmo eixo — e o eixo é a etapa, não o insumo nem a conta contábil.
 *
 * ## O previsto
 *
 * `orcamentos_itens.valor` da linha que gerou a parte, lido por junção. Nulo na
 * etapa digitada à mão, que nunca foi orçada — e nulo **não é zero**: zero diria
 * "orçamos nada para isto", e o que se quer dizer é "não há com o que comparar".
 *
 * ## O realizado, e por que ele tem três parcelas
 *
 * Elas chegam separadas em vez de somadas porque **respondem a perguntas
 * diferentes quando o número surpreende**. Etapa estourada por `consumo` é
 * material demais aplicado; por `servico`, é subempreitada mais cara do que se
 * orçou; por `avulso`, é custo que alguém lançou direto no financeiro. São três
 * conversas com três pessoas diferentes, e um total só mandaria procurar.
 *
 * - **`consumo`** — `lancamentos_consumo_itens.valor_total` das linhas
 *   apropriadas à etapa, **menos** o que voltou por devolução de consumo à mesma
 *   etapa (decisão 82). É quantidade × custo médio vigente na baixa, e não se
 *   reprecifica depois.
 * - **`servico`** — a fatia rateada dos itens de nota de compra confirmada cuja
 *   operação fiscal **não movimenta estoque**. É a subempreitada e o serviço
 *   comprados por nota.
 * - **`avulso`** — a fatia rateada dos títulos a pagar que **não vieram de nota
 *   de compra**: lançamento manual, contrato de pagamento, mão de obra.
 *
 * **O material comprado por nota não entra em nenhuma das três**, e a ausência é
 * a regra inteira: ele já vai entrar como `consumo` quando for aplicado. Contar
 * a nota *e* o consumo somaria o mesmo cimento duas vezes — uma ao comprar e
 * outra ao usar —, e a etapa apareceria com o dobro do custo. É a mesma razão de
 * `servico` existir como parcela própria em vez de "todo título de nota": a nota
 * de serviço não gera consumo nenhum, e excluí-la inteira faria a subempreitada
 * desaparecer da etapa.
 *
 * ## O período do painel **não** recorta este bloco
 *
 * É a única exceção da tela junto com a contagem de obras, e pelo mesmo tipo de
 * razão: o previsto não tem data — ele é o valor da linha do orçamento, inteiro.
 * Recortar só o realizado compararia um total contra uma fatia, e toda etapa
 * pareceria barata em qualquer período curto. O realizado aqui é **acumulado
 * desde sempre**, e a tela diz isso.
 *
 * ## Só com uma obra escolhida
 *
 * Etapa pertence a uma obra, e "Fundação" existe em todas elas. Sem
 * `obraId` no filtro, a lista viria com a fundação de quarenta canteiros
 * empilhada sem nada distinguindo uma da outra. O bloco vem vazio, e a tela
 * pede a obra.
 */
export const etapaDoConfrontoSchema = z.object({
  /**
   * A parte da obra, ou `null` na linha do que **não** tem etapa.
   *
   * Essa linha existe de propósito: custo rateado à obra sem apontar etapa é
   * comum — é o que a nota de almoxarifado central e o título geral produzem —,
   * e escondê-lo faria a soma das linhas não fechar com o custo da obra. Quem a
   * vê grande sabe que há apropriação a fazer. É o mesmo raciocínio da barra
   * "Sem conta contábil" dos gráficos de consumo.
   */
  obraParteId: uuidSchema.nullable(),
  nome: z.string(),
  /** O valor da linha do orçamento que gerou a etapa. Nulo: etapa não orçada. */
  previsto: dinheiroSchema.nullable(),
  consumo: dinheiroSchema,
  servico: dinheiroSchema,
  avulso: dinheiroSchema,
  /**
   * O que já se **faturou** desta etapa: o valor dos títulos a receber rateados
   * a ela (`.spec/015`, decisão 68).
   *
   * `contas_receber.valor` ponderado pelo índice do rateio, e não o saldo: a
   * pergunta é quanto a etapa já gerou de receita, e o título já recebido gerou
   * a mesma receita do título em aberto. É a mesma leitura de `avulso`, do lado
   * que paga.
   *
   * ## Zero não quer dizer que a obra não fatura
   *
   * A etapa só recebe receita quando alguém **rateia o título a ela** — a
   * medição por etapa é isso. O faturamento gerado pela obra inteira (decisão
   * 58) rateia 100% na obra sem apontar etapa, e por isso cai na linha "Sem
   * etapa", onde continua somando no rodapé. Uma obra faturada em bloco mostra
   * receita só ali, e está certo: não há como dizer qual etapa vendeu o quê.
   */
  faturado: dinheiroSchema,
  /**
   * Quanto desse faturamento **entrou**: as baixas não estornadas, pelo
   * principal, ponderadas pelo mesmo índice.
   *
   * `valor_baixa_saldo_titulo` — juros, multa e desconto ficam de fora, como em
   * todo indicador desta tela: eles não são receita de obra.
   *
   * Separado de `faturado` porque "medi e não recebi" é outra conversa, com
   * outra pessoa, do que "não medi". A diferença entre os dois é o que a etapa
   * tem a receber, e sai por subtração.
   */
  recebido: dinheiroSchema,
});

export type EtapaDoConfronto = z.infer<typeof etapaDoConfrontoSchema>;

/**
 * As três parcelas somadas.
 *
 * Função, e não coluna na resposta: o servidor já devolve as três, e um quarto
 * campo com a soma delas é o que um dia discorda — basta uma parcela nova entrar
 * e alguém esquecer de incluí-la no total. Aqui não há como esquecer.
 *
 * Mora no contrato porque a tela soma para mostrar e o servidor soma para
 * ordenar as linhas pela diferença. Duas implementações divergiriam num
 * arredondamento, e a ordem da tabela deixaria de corresponder aos números dela.
 */
export function realizadoDaEtapa(etapa: EtapaDoConfronto): string {
  return somarDinheiro([etapa.consumo, etapa.servico, etapa.avulso]);
}

/**
 * Previsto − realizado. **Positivo sobra, negativo estourou.**
 *
 * Nulo quando não há previsto: a etapa digitada à mão gastou o que gastou, e
 * chamar isso de estouro seria inventar um orçamento que ninguém escreveu. A
 * tela mostra um traço.
 */
export function diferencaDaEtapa(etapa: EtapaDoConfronto): string | null {
  if (etapa.previsto === null) return null;

  return subtrairDinheiro(etapa.previsto, realizadoDaEtapa(etapa));
}

/**
 * O avanço da etapa em percentual: **realizado ÷ previsto** (`.spec/015`,
 * decisão 68).
 *
 * ## É avanço de custo, e não de obra feita
 *
 * O sistema não sabe quanto de concreto foi lançado — sabe quanto se gastou. O
 * número responde "quanto do previsto desta etapa já foi consumido", que é o
 * que os dados sustentam, e a tela o chama assim. Ler isto como percentual
 * físico é o erro que ele convida, e é justamente por isso que o rótulo na tela
 * e no relatório diz "do previsto".
 *
 * Passar de 100 é informação, não defeito: é a etapa que estourou, e o número
 * continua sendo dito. Truncar em 100 esconderia exatamente o caso que
 * interessa.
 *
 * ## Nulo em dois casos, e os dois são "não há como dividir"
 *
 * Sem previsto — etapa digitada à mão — não há denominador. Previsto zero
 * também não: dividir por zero daria infinito, e uma etapa orçada em zero que
 * gastou mil não tem percentual, tem um problema no orçamento. A tela mostra um
 * traço nos dois.
 *
 * Devolve **número**, e não string de dinheiro: percentual não é dinheiro, e
 * `somarDinheiro` sobre ele não quer dizer nada — o avanço do total se calcula
 * dos totais, nunca somando os das linhas.
 */
export function avancoDaEtapa(etapa: EtapaDoConfronto): number | null {
  return avancoDe(etapa.previsto, realizadoDaEtapa(etapa));
}

/**
 * A mesma conta, com os dois valores em mãos.
 *
 * Existe para o **rodapé**: o avanço do total é `total realizado ÷ total
 * previsto`, e nunca a média dos avanços das linhas — uma etapa de mil a 200% e
 * outra de um milhão a 50% dariam 125% na média e 50,1% na conta certa.
 *
 * Exportada em vez de resolvida montando uma etapa falsa com o realizado
 * enfiado em `consumo`: a etapa falsa funciona e mente sobre o que está sendo
 * somado, e é o tipo de coisa que alguém "corrige" seis meses depois.
 *
 * `Number` aqui é exibição, não dinheiro: o resultado é um percentual que só vai
 * ser escrito na tela. Nenhum valor monetário sai desta função.
 */
export function avancoDe(previsto: string | null, realizado: string): number | null {
  if (previsto === null) return null;
  if (compararDinheiro(previsto, '0.00') === 0) return null;

  return (Number(realizado) / Number(previsto)) * 100;
}

/* -------------------------------------------------------------------------- */
/* Cronograma — previsto por vencimento × realizado por baixa                 */
/* -------------------------------------------------------------------------- */

/**
 * Um mês do cronograma financeiro da obra (`.spec/015`, decisão 72).
 *
 * É o bloco Financeiro **aberto no tempo**: em vez de dois totais do período,
 * uma linha por mês dizendo quanto estava previsto para se mover e quanto se
 * moveu de fato, dos dois lados. É o que responde *quando* a obra precisa de
 * caixa — a pergunta que um total do trimestre não responde.
 *
 * ## Previsto é o **valor do título na data de vencimento**, e não o saldo
 *
 * É a diferença mais importante em relação ao bloco Financeiro, e ela é
 * deliberada. Lá, "em aberto" é o **saldo** do que ainda deve alguma coisa —
 * compromisso vivo. Aqui, o previsto é o cronograma **como foi combinado**: a
 * parcela de março entra em março pelo valor dela, tenha sido paga ou não.
 *
 * Com o saldo, o mês já quitado apareceria com previsto zero e realizado cheio
 * — "pagaram algo que não era devido" —, e a aderência ao cronograma, que é a
 * única coisa que este bloco existe para mostrar, ficaria ilegível. Os dois
 * números respondem perguntas diferentes e por isso divergem; ver a nota que a
 * tela e o relatório imprimem.
 *
 * ## Realizado é a baixa, pela data da movimentação
 *
 * O principal (`valor_baixa_saldo_titulo`), **sem** juros, multa e desconto, e
 * **sem** as baixas estornadas — os mesmos recortes de todo indicador desta
 * tela. Pela data em que o dinheiro se moveu, e não pela de lançamento: a baixa
 * feita hoje com data de ontem saiu ontem.
 *
 * ## O mês é `AAAA-MM`
 *
 * Texto, e não data: o mês não é um dia, e escolher o dia 1º para representá-lo
 * convidaria alguém a formatá-lo como data e a ler "01/03" onde está escrito
 * "março". `AAAA-MM` ordena sozinho, como toda data de negócio do projeto.
 */
export const mesDoCronogramaSchema = z.object({
  /** `AAAA-MM`. */
  mes: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'O mês deve estar no formato AAAA-MM.'),
  /** Títulos a receber que **vencem** neste mês, pela fatia rateada da obra. */
  aReceberPrevisto: dinheiroSchema,
  /** Baixas de recebimento **movimentadas** neste mês, pelo principal rateado. */
  aReceberRealizado: dinheiroSchema,
  aPagarPrevisto: dinheiroSchema,
  aPagarRealizado: dinheiroSchema,
});

export type MesDoCronograma = z.infer<typeof mesDoCronogramaSchema>;

/** `a receber − a pagar` do que estava previsto para o mês. */
export function liquidoPrevisto(mes: MesDoCronograma): string {
  return subtrairDinheiro(mes.aReceberPrevisto, mes.aPagarPrevisto);
}

/** `a receber − a pagar` do que de fato se moveu no mês. */
export function liquidoRealizado(mes: MesDoCronograma): string {
  return subtrairDinheiro(mes.aReceberRealizado, mes.aPagarRealizado);
}

/** Um mês sem movimento nenhum. */
function mesVazio(mes: string): MesDoCronograma {
  return {
    mes,
    aReceberPrevisto: '0.00',
    aReceberRealizado: '0.00',
    aPagarPrevisto: '0.00',
    aPagarRealizado: '0.00',
  };
}

/** `AAAA-MM` → o mês seguinte, virando o ano em dezembro. */
function proximoMes(mes: string): string {
  const ano = Number(mes.slice(0, 4));
  const numero = Number(mes.slice(5, 7));

  return numero === 12
    ? `${String(ano + 1)}-01`
    : `${String(ano)}-${String(numero + 1).padStart(2, '0')}`;
}

/**
 * Preenche os meses sem movimento entre o primeiro e o último (`.spec/015`,
 * decisão 72).
 *
 * O servidor devolve **só os meses que têm alguma coisa** — é o que um `GROUP
 * BY` produz. Desenhados assim, março e julho apareceriam vizinhos, e a tabela
 * se leria como se abril, maio e junho não existissem. O que eles significam é
 * "nada se move", que é informação de cronograma: é justamente o vale entre
 * duas medições.
 *
 * **Não estende para fora dos extremos.** Zerar até o fim do período escolhido
 * encheria a tabela de meses futuros que ninguém planejou, e zerar até hoje
 * inventaria passado. A faixa é a dos títulos da obra, que é o que existe.
 *
 * Função do contrato porque a tela e o relatório desenham a mesma tabela — duas
 * implementações divergiriam no primeiro dezembro, que é onde a virada de ano
 * erra.
 */
export function mesesContinuos(meses: readonly MesDoCronograma[]): readonly MesDoCronograma[] {
  const primeiro = meses[0];
  const ultimo = meses[meses.length - 1];
  if (primeiro === undefined || ultimo === undefined) return meses;

  const porMes = new Map(meses.map((linha) => [linha.mes, linha]));
  const completos: MesDoCronograma[] = [];

  for (let mes = primeiro.mes; mes <= ultimo.mes; mes = proximoMes(mes)) {
    completos.push(porMes.get(mes) ?? mesVazio(mes));
  }

  return completos;
}

/* -------------------------------------------------------------------------- */
/* A resposta                                                                 */
/* -------------------------------------------------------------------------- */

export const painelDeObrasSchema = z.object({
  /**
   * O período, devolvido **como veio** — `null` na ponta que o usuário não
   * escolheu.
   *
   * Não é a data do primeiro ou do último movimento: a tela precisa distinguir
   * "desde o começo da obra" de uma data escolhida, e devolver a data real faria
   * o usuário concluir que não há histórico antes dela. É a mesma escolha do
   * cardex, que também ecoa o período pedido.
   */
  de: dataSchema.nullable(),
  ate: dataSchema.nullable(),
  /**
   * Quantas obras o recorte alcança.
   *
   * Não é decoração: sem ele, "R$ 4.200.000,00 a pagar" não diz se é uma obra ou
   * quarenta, e a primeira coisa que alguém faz com o número é dividi-lo pelo que
   * imagina ser a quantidade. Conta **as obras do filtro**, inclusive as que não
   * têm título nem consumo no período — é o denominador, e uma obra parada
   * pertence a ele.
   *
   * **O período não a recorta**, e é a única exceção da tela: obra não tem data
   * de movimento, e filtrá-la pelo cronograma faria a contagem mudar de
   * significado — passaria a ser "obras em andamento no período", que é outra
   * pergunta e exigiria decidir o que fazer com obra sem data de início.
   */
  obras: z.number().int().nonnegative(),
  /**
   * O que ainda tem saldo e **vence no período**, dos dois lados.
   *
   * Pela data de vencimento, que é quando o dinheiro está previsto para se
   * mover. O valor é a fatia rateada do **saldo**, e não do valor original: o
   * título de R$ 1.000,00 com R$ 700,00 já pagos entra por R$ 300,00, e depois
   * pelo índice da obra.
   *
   * `liquido` é `aReceber − aPagar`, e é negativo no período em que a obra deve
   * mais do que tem a receber — o caso normal de canteiro em execução.
   */
  emAberto: confrontoSchema,
  /**
   * O que de fato saiu e entrou no período: baixas não estornadas, pelo
   * **principal**, ponderadas pela fatia de rateio da obra.
   *
   * Pela data da movimentação da baixa. Juros, multa e desconto ficam de fora —
   * eles não são do título, e quem os quer tem o extrato da conta corrente
   * (FIN·02).
   */
  realizado: confrontoSchema,
  /**
   * As obrigações do lado que paga, abertas por conta contábil.
   *
   * Uma linha por conta contábil dos títulos que vencem no período, com o valor
   * do título, o quanto já foi pago dele e o saldo. **`valor` é `pago +
   * emAberto` em toda linha**, e a coluna `emAberto` fecha com `emAberto.aPagar`
   * — a menos do centavo que o arredondamento por conta pode produzir, porque a
   * tabela pondera duas vezes e arredonda em cada conta. Ver
   * `linhaDeContaContabilSchema`.
   */
  porContaContabil: z.array(linhaDeContaContabilSchema),
  /** O total consumido no período: quantos lançamentos e quanto de material. */
  consumo: totalDoConsumoSchema,
  /**
   * O consumo distribuído pela conta contábil **do insumo**.
   *
   * A classificação vem de `insumos.conta_contabil_id`, que é opcional: o insumo
   * cadastrado às pressas não a tem, e o consumo dele cai na fatia "Sem conta
   * contábil". Ela aparece com esse nome em vez de sumir — material sem natureza
   * contábil é justamente o que o balancete de obra vai cobrar, e uma barra a
   * menos esconderia dinheiro que o indicador do topo continua contando.
   */
  consumoPorContaContabil: serieDeValoresSchema,
  /**
   * O consumo distribuído pelo grupo **imediato** do insumo.
   *
   * O grupo é uma árvore, e este gráfico usa a folha em que o insumo está
   * pendurado — não a raiz dela. Subir até a raiz daria cinco barras enormes
   * ("Materiais", "Serviços") que não distinguem cimento de material elétrico, e
   * é justamente essa distinção que faz o gráfico servir para alguma coisa. A
   * fatia "Outros" já resolve a cauda longa.
   */
  consumoPorGrupoDeInsumos: serieDeValoresSchema,
  /**
   * Orçado × realizado por etapa da obra (`.spec/015`, decisão 60).
   *
   * **Vazio sem `obraId` no filtro**, e vazio não é ausência de dado: é a
   * resposta certa para uma pergunta que não foi feita sobre uma obra — etapa
   * pertence a uma obra, e "Fundação" existe em todas elas. A tela pede a obra
   * em vez de mostrar uma tabela vazia.
   *
   * **O período não o recorta.** Ver `etapaDoConfrontoSchema`: o previsto não
   * tem data, e comparar um total contra uma fatia faria toda etapa parecer
   * barata em qualquer período curto.
   *
   * A ordem é a da proposta — o número da linha do orçamento —, com as etapas
   * não orçadas depois e a linha sem etapa por último.
   */
  confrontoPorEtapa: z.array(etapaDoConfrontoSchema),
  /**
   * O cronograma financeiro, mês a mês (`.spec/015`, decisão 72).
   *
   * Em ordem crescente, e **só com os meses que têm movimento** — quem preenche
   * os vales é `mesesContinuos`, no cliente que desenha.
   *
   * **O período recorta este bloco**, ao contrário do confronto por etapa: aqui
   * as duas pontas têm data, e recortar as duas pelo mesmo intervalo compara
   * coisas comparáveis. O relatório de março mostra março.
   *
   * Vazio na obra sem título nenhum — e vazio também quando o recorte não
   * alcança nenhum vencimento nem nenhuma baixa, que é uma resposta e não uma
   * falha.
   */
  cronograma: z.array(mesDoCronogramaSchema),
  /**
   * O faturamento por nota de serviço das obras do filtro: quanto elas valem,
   * quanto já saiu em nota e quanto falta (`.spec/015`, decisão 95).
   *
   * **Este bloco ignora o período**, como o confronto por etapa, e pelo mesmo
   * motivo: o valor a faturar da obra não tem data. "Saldo a faturar" é
   * posição — o que falta hoje —, e recortar só o faturado compararia um total
   * contra uma fatia. O que recorta é o filtro de empresa, cliente e obra.
   *
   * Os números são os do saldo a faturar da nota de serviço (decisão 94),
   * somados: "faturado" é a nota emitida que gerou título, pelo serviço menos o
   * desconto incondicionado. **Não é o "A receber" do topo**, que é saldo de
   * título por vencimento — a nota faturada e já recebida continua faturada.
   *
   * Só as obras **com valor que faturam por nota** entram nos três números;
   * as sem valor e as que faturaram pelo cadastro vêm contadas à parte. Ver
   * `faturamentoPorNotaSchema`.
   *
   * **`null` para quem não vê o saldo a faturar** (`.spec/015`, decisões 99 e
   * 100). O painel tem a permissão dele (`contratos.painel_obra.ver`), e o
   * faturamento por nota segue a do saldo (`faturamento.saldo_a_faturar.ver`)
   * — a mesma regra de `faturadoEmNotas` na obra e no contrato (decisão 97).
   * `null`, e não um bloco de zeros, que diria que as obras não faturaram.
   */
  faturamentoPorNota: faturamentoPorNotaSchema.nullable(),
});

export type PainelDeObras = z.infer<typeof painelDeObrasSchema>;
